import React, { useState, useEffect } from 'react';
import { doc, updateDoc, collection, addDoc, getDocs, orderBy, query, getDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, Storage, auth } from '../firebase';
import { FaTrash } from 'react-icons/fa';
import '../style/Order.css';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';


const NeckLabels = () => {
  const [user, setUser] = useState(null);
  const [isNeckLabelEnabled, setIsNeckLabelEnabled] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedDesigns, setUploadedDesigns] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [designToDelete, setDesignToDelete] = useState(null);

  // Authenticate user on component mount
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      setUser(currentUser);
    }
  }, []);

  // Fetch initial neck label status and uploaded designs
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;

      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        const userData = userDoc.data();
        
        // Set neck labels status
        setIsNeckLabelEnabled(userData?.neckLabels || false);

        // Fetch all uploaded designs
        const brandingRef = collection(db, 'users', user.uid, 'branding');
        const brandingQuery = await getDocs(query(brandingRef, orderBy('timestamp', 'desc')));
        
        const designs = brandingQuery.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUploadedDesigns(designs.filter(design => design.Neck_label));
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, [user]);

  // If user is not authenticated, show login prompt
  if (!user) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden md:ml-64">
          <Header />
          <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center max-w-md">
              <div className="w-16 h-16 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">Authentication Required</h2>
              <p className="text-gray-600">Please log in to access neck labels functionality.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Toggle neck labels in Firestore
  const handleToggleNeckLabels = async () => {
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        neckLabels: !isNeckLabelEnabled
      });
      setIsNeckLabelEnabled(!isNeckLabelEnabled);
    } catch (error) {
      console.error("Error updating neck labels status:", error);
    }
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Create a preview of the file
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedFile({
          file: file,
          preview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload design to Firebase Storage and Firestore
  const handleDesignUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    try {
      // Create a storage reference
      const storageRef = ref(Storage, `neck_labels/${user.uid}/${selectedFile.file.name}`);
      
      // Upload file
      const uploadTask = uploadBytesResumable(storageRef, selectedFile.file);
      
      uploadTask.on('state_changed', 
        (snapshot) => {
          // Track upload progress
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error("Upload error:", error);
        },
        async () => {
          // Get download URL after successful upload
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          
          // Store design metadata in Firestore
          const brandingRef = collection(db, 'users', user.uid, 'branding');
          const docRef = await addDoc(brandingRef, {
            productName: 'Neck Label',
            Neck_label: downloadURL,
            timestamp: new Date()
          });

          // Update uploaded designs
          const newDesign = {
            id: docRef.id,
            productName: 'Neck Label',
            Neck_label: downloadURL,
            timestamp: new Date()
          };
          setUploadedDesigns(prev => [newDesign, ...prev]);

          // Reset state
          setSelectedFile(null);
          setUploadProgress(0);
          alert('Design uploaded successfully!');
        }
      );
    } catch (error) {
      console.error("Error uploading design:", error);
    }
  };

  // Show confirmation modal before deleting
  const showDeleteConfirmation = (designId, imageUrl) => {
    setDesignToDelete({ id: designId, url: imageUrl });
    setShowConfirmModal(true);
  };

  // Delete design from Firebase Storage and Firestore
  const handleDeleteDesign = async () => {
    if (!designToDelete) return;

    try {
      // Delete from Firestore
      const docRef = doc(db, 'users', user.uid, 'branding', designToDelete.id);
      await deleteDoc(docRef);

      // Delete from Firebase Storage
      const imageRef = ref(Storage, designToDelete.url);
      await deleteObject(imageRef);

      // Update local state
      setUploadedDesigns(prev => prev.filter(design => design.id !== designToDelete.id));
      
      // Reset modal state
      setShowConfirmModal(false);
      setDesignToDelete(null);
      
      alert('Design deleted successfully!');
    } catch (error) {
      console.error("Error deleting design:", error);
      alert('Error deleting design. Please try again.');
    }
  };

  // Cancel delete operation
  const cancelDelete = () => {
    setShowConfirmModal(false);
    setDesignToDelete(null);
  };

  return (
   <div className="flex h-screen bg-white">
  <Sidebar />
  <div className="flex-1 flex flex-col min-w-0 overflow-hidden md:ml-64">
    <Header />
    <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {/* Header Section */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2 create-order-title text-left">Neck Labels</h1>
                  <p className="text-gray-600 text-lg">
                    We print your neck labels on demand • <span className="font-semibold text-black">₹5 per piece</span>
                  </p>
                </div>
                
                <div className="flex items-center gap-4">
                  <span className={`text-sm font-medium transition-colors duration-200 ${
                    isNeckLabelEnabled ? 'text-gray-600' : 'text-gray-500'
                  }`}>
                    {isNeckLabelEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      id="neck-label-toggle"
                      checked={isNeckLabelEnabled} 
                      onChange={handleToggleNeckLabels}
                      className="sr-only"
                    />
                    <label 
                      htmlFor="neck-label-toggle" 
                      className={`relative inline-flex h-7 w-12 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 ${
                        isNeckLabelEnabled ? 'bg-black' : 'bg-gray-300'
                      }`}
                    >
                      <span 
                        className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out ${
                          isNeckLabelEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
     
            {isNeckLabelEnabled && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2 create-order-subtitle">Upload Neck Label Design</h2>
                  <p className="text-gray-600">Upload your neck label design (2 × 2 inches)</p>
                </div>

                {/* File Upload Area */}
                <div className="flex flex-col items-center">
                  <div 
                    className={`relative w-48 h-48 border-2 border-dashed rounded-2xl transition-all duration-300 ease-in-out cursor-pointer group ${
                      selectedFile 
                        ? 'border-black bg-gray-50' 
                        : 'border-gray-300 hover:border-black hover:bg-gray-50'
                    }`}
                  >
                    <input 
                      type="file" 
                      id="file-upload"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />

                    {!selectedFile && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                        <div className="w-12 h-12 mb-4 text-gray-400 group-hover:text-black transition-colors duration-200">
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                            className="w-full h-full"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth={1.5} 
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
                            />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500 group-hover:text-gray-700 transition-colors duration-200 text-center">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB</p>
                      </div>
                    )}

                    {selectedFile && (
                      <div className="absolute inset-0 p-2">
                        <img 
                          src={selectedFile.preview} 
                          alt="Selected design" 
                          className="w-full h-full object-cover rounded-xl"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-xl flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="bg-white rounded-full p-2 shadow-lg">
                              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Upload Button */}
                  {selectedFile && (
                    <button 
                      onClick={handleDesignUpload}
                      className="mt-6 bg-black hover:bg-gray-800 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    >
                      Upload Design
                    </button>
                  )}

                  {/* Upload Progress */}
                  {uploadProgress > 0 && (
                    <div className="mt-6 w-full max-w-md">
                      <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div 
                          className="bg-black h-full rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-2 text-center">
                        Uploading: {Math.round(uploadProgress)}%
                      </p>
                    </div>
                  )}
                </div>

                {/* Uploaded Designs Gallery */}
                {uploadedDesigns.length > 0 && (
                  <div className="mt-12">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-gray-900">Your Neck Labels</h3>
                      <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                        {uploadedDesigns.length} design{uploadedDesigns.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {uploadedDesigns.map((design, index) => (
                        <div 
                          key={design.id} 
                          className="group relative aspect-square bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                        >
                          <img 
                            src={design.Neck_label} 
                            alt={`Neck Label ${index + 1}`} 
                            className="w-full h-full object-cover"
                          />
                          
                          {/* Overlay with delete button */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                            <button
                              onClick={() => showDeleteConfirmation(design.id, design.Neck_label)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transform hover:scale-110"
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                          </div>
                          
                          {/* Design number badge */}
                          <div className="absolute top-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded-full">
                            #{index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100">
            <div className="p-8">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                  <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">Delete Neck Label</h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Are you sure you want to delete this neck label? This action cannot be undone and will permanently remove the design from your account.
                </p>
                
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={cancelDelete}
                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-200 transform hover:scale-105"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteDesign}
                    className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg"
                  >
                    Delete Design
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default NeckLabels;